import { REF_SIZE } from '../../../constants/size';
import { LoadSingleTexture } from '../../../../vendors/ngab/load/LoadSingleTexture';
import { LoadTextures } from '../../../../vendors/ngab/load/LoadTextures';

const THREE = require('three');

//
// Prepared geometries storage:
// (e.g. preloaded)

Object.defineProperty(HTMLMediaElement.prototype, 'playing', {
    get() {
        return !!(this.currentTime > 0 && !this.paused && !this.ended && this.readyState > 2);
    }
});

const texturesMap = new Map();
const texturesOptionMap = new Map();
const textureVideos = [];
let pixelRatio = 1;

const chooseSizeOption = (options) => {
    if (options.length < 1) return null;
    if (options.length < 2) return options[0];

    const winW = window.innerWidth * pixelRatio;
    const winH = window.innerHeight * pixelRatio;

    const ratio = (winW < REF_SIZE.w * 0.5 || winH < REF_SIZE.h * 0.5) ? 0.5 : 1;
    const optionsSorted = options.sort((a, b) => b.ratio - a.ratio);

    // console.log(optionsSorted, optionsSorted.find((a) => a.ratio <= ratio) ?? options[0]);

    return optionsSorted.find((a) => a.ratio <= ratio) ?? optionsSorted[0];
};

const createSource = (data) => {
    const el = document.createElement('SOURCE');
    const { type, src } = data;

    el.setAttribute('type', type);
    el.setAttribute('src', src);

    return el;
};

const createVideo = (data/* , errorCallback */) => {
    const el = document.createElement('VIDEO');
    const { source } = data;

    const addSource = (tag) => {
        // tag.addEventListener('error', errorCallback);
        el.appendChild(tag);
    };

    if (source instanceof Array) {
        source.forEach((s) => {
            addSource(createSource(s));
        });
    } else if (typeof source === 'object') {
        addSource(createSource(source));
    } else if (typeof source === 'string') {
        addSource(createSource({
            src: source
        }));
    }

    el.setAttribute('playsinline', 'playsinline');
    el.setAttribute('playsInline', 'playsInline');
    el.setAttribute('webkit-playsinline', 'true');
    el.setAttribute('preload', 'meta');
    el.setAttribute('loop', 'loop');
    el.muted = 'muted';

    el.style.position = 'fixed';
    el.style.top = '0';
    el.style.height = '1px';

    return el;
};

const addSourcesEventListeners = (video, event, handler) => {
    if (!video) return;

    const sources = [...video.querySelectorAll('source')];

    sources.forEach((s) => {
        s.addEventListener(event, handler);
    });
};

const removeSourcesEventListeners = (video, event, handler) => {
    if (!video) return;

    const sources = [...video.querySelectorAll('source')];

    sources.forEach((s) => {
        s.removeEventListener(event, handler);
    });
};

export const TexturesStorage = {
    async getTexture(data, name) {
        const { options } = data;

        if (options < 1) return null;

        name = name ?? options[0].src;

        const newOption = chooseSizeOption(options);

        if (texturesOptionMap.has(name)) {
            const curOption = texturesOptionMap.get(name);

            // This particular texture has already been loaded
            if (curOption === newOption) return texturesMap.get(name);
        }

        let texture;
        let newOptionImageSrc = null;
        const newOptionPosterSrc = newOption.poster?.src;

        if (newOption.type === 'video') {
            let videoHasError = false;
            let videoCanplayHandler = null;
            let videoErrorRejectHandler = null;
            const videoErrorHandler = () => {
                videoHasError = true;
                newOptionImageSrc = newOptionPosterSrc;
            };

            const video = createVideo(newOption);
            let root = document.querySelector('.video-textures');

            if (!root) root = document.body;
            root.appendChild(video);

            if (video instanceof HTMLVideoElement) {
                if (!video.playing) {
                    try {
                        await new Promise((resolve, reject) => {
                            videoCanplayHandler = () => {
                                videoHasError = false;
                                newOptionImageSrc = null;

                                // console.log('CAN PLAY', video.children[0].getAttribute('src'));

                                resolve();
                            };

                            videoErrorRejectHandler = (evt) => {
                                reject(evt);
                            };

                            video.addEventListener('loadedmetadata', videoCanplayHandler);
                            addSourcesEventListeners(video, 'error', videoErrorRejectHandler);

                            texture = new THREE.VideoTexture(video);
                            texture.minFilter = THREE.LinearFilter;
                            texture.magFilter = THREE.LinearFilter;
                            texture.format = THREE.RGBFormat;

                            video.load();
                        });
                    } catch (e) {
                        videoErrorHandler(e);
                    }

                    if (!videoHasError) {
                        try {
                            await video.play();
                        } catch (e) {
                            console.warn(e);

                            // Set Image Texture instead
                            videoErrorHandler();
                        }
                    }
                }

                if (videoCanplayHandler) video.removeEventListener('loadedmetadata', videoCanplayHandler);
                if (videoErrorRejectHandler) removeSourcesEventListeners(video, 'error', videoErrorRejectHandler);

                if (videoHasError) {
                    if (video.parentNode) video.parentNode.removeChild(video);
                } else {
                    texturesOptionMap.set(name, newOption);
                    texturesMap.set(name, texture);
                    textureVideos.push(video);
                }
            } else {
                videoErrorHandler();
            }
        } else {
            newOptionImageSrc = newOption.src;
        }

        if (newOptionImageSrc) {
            texture = await LoadSingleTexture(name, newOptionImageSrc, false, true, false)
                .then((txtr) => {
                    texturesOptionMap.set(name, newOption);
                    texturesMap.set(name, txtr);

                    return txtr;
                });
        }

        return texture;
    },

    setPixelRatio(value) {
        if (typeof value !== 'number' || Number.isNaN(value)) return;

        pixelRatio = value;
    },

    startVideos() {
        if (textureVideos) {
            textureVideos.forEach((video) => {
                if (!video.playing) {
                    video.load();

                    try {
                        video.play();
                    } catch (e) {
                        console.warn(e);
                    }
                }
            });
        }
    },

    async preload(collection, progressUpdate) {
        // Set the manifest preloading here

        let texturesLoaded;
        const optionsSelected = collection.map((obj) => {
            const option = chooseSizeOption(obj.textures);

            return { name: obj.name, url: option.src, option };
        }).filter((obj) => obj.option.type !== 'video');

        try {
            texturesLoaded = await LoadTextures(optionsSelected, true, true, progressUpdate, false);
        } catch (e) {
            console.warn('Atlas loading failed', e);
        }

        if (!texturesLoaded) return [];

        const names = Object.keys(texturesLoaded);

        names.forEach((name, index) => {
            const item = texturesLoaded[name];

            texturesOptionMap.set(name, optionsSelected[index].option);
            texturesMap.set(name, item.texture);

        });

        return texturesLoaded;
    }
};
